import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MetricCard from '../components/MetricCard';
import { usePortal } from '../context/PortalContext';
import { clubService } from '../services/clubService';
import { tableService } from '../services/tableService';
import { Club, ClubCounterSnapshot, ClubMember, TableSummary } from '../types';
import { formatCompactCurrency, formatCurrency, formatDateTime, getGameTypeLabel, getMemberRoleLabel } from '../utils/formatters';

const getTableStatusLabel = (value: TableSummary['status']): string => {
  switch (value) {
    case 'ACTIVE':
      return 'Ativa';
    case 'PAUSED':
      return 'Pausada';
    case 'CLOSED':
      return 'Finalizada';
    default:
      return value || '-';
  }
};

const getTableStatusTone = (value: TableSummary['status']): string => {
  switch (value) {
    case 'ACTIVE':
      return 'portal-status-pill--positive';
    case 'PAUSED':
      return 'portal-status-pill--warning';
    case 'CLOSED':
      return 'portal-status-pill--neutral';
    default:
      return 'portal-status-pill--neutral';
  }
};

const DASHBOARD_REFRESH_INTERVAL_MS = 5000;

const DashboardPage: React.FC = () => {
  const { selectedClub } = usePortal();
  const [club, setClub] = useState<Club | null>(null);
  const [tables, setTables] = useState<TableSummary[]>([]);
  const [members, setMembers] = useState<ClubMember[]>([]);
  const [counterSnapshot, setCounterSnapshot] = useState<ClubCounterSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [liveRefreshEnabled, setLiveRefreshEnabled] = useState(true);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [error, setError] = useState('');
  const requestIdRef = useRef(0);

  const loadDashboardData = useCallback(
    async (options: { silent?: boolean } = {}) => {
      if (!selectedClub?.id) {
        setClub(null);
        setTables([]);
        setMembers([]);
        setCounterSnapshot(null);
        setLastSyncedAt(null);
        return;
      }

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      if (options.silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError('');

      try {
        const [clubPayload, tablesPayload, membersPayload, counterPayload] = await Promise.all([
          clubService.getClub(selectedClub.id),
          tableService.getClubTables(selectedClub.id),
          clubService.getClubMembers(selectedClub.id),
          clubService.getCounterSnapshot(selectedClub.id),
        ]);

        if (requestIdRef.current !== requestId) {
          return;
        }

        setClub(clubPayload);
        setTables(tablesPayload);
        setMembers(membersPayload);
        setCounterSnapshot(counterPayload);
        setLastSyncedAt(new Date().toISOString());
      } catch (nextError: any) {
        if (requestIdRef.current !== requestId) {
          return;
        }
        setError(nextError.message || 'Erro ao carregar dashboard do club');
      } finally {
        if (requestIdRef.current === requestId) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [selectedClub?.id]
  );

  useEffect(() => {
    void loadDashboardData();
  }, [loadDashboardData]);

  useEffect(() => {
    if (!selectedClub?.id || !liveRefreshEnabled) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      if (document.hidden) {
        return;
      }

      void loadDashboardData({ silent: true });
    }, DASHBOARD_REFRESH_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [loadDashboardData, liveRefreshEnabled, selectedClub?.id]);

  useEffect(() => {
    if (!selectedClub?.id || !liveRefreshEnabled) {
      return undefined;
    }

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        void loadDashboardData({ silent: true });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadDashboardData, liveRefreshEnabled, selectedClub?.id]);

  const activeTables = useMemo(
    () => tables.filter((table) => table.status === 'ACTIVE').length,
    [tables]
  );

  const activePlayers = useMemo(
    () => tables.reduce((sum, table) => sum + (table.currentPlayers || 0), 0),
    [tables]
  );

  const totalWalletTracked = useMemo(
    () => (counterSnapshot?.players || []).reduce((sum, player) => sum + player.wallet_balance, 0),
    [counterSnapshot]
  );

  if (!selectedClub) {
    return (
      <section className="portal-section-card">
        <h3>Nenhum club selecionado</h3>
        <p className="portal-muted">Selecione um club no topo para iniciar a operacao.</p>
      </section>
    );
  }

  return (
    <div className="portal-page-grid portal-page-stack">
      <section className="portal-section-card portal-section-card--hero portal-section-card--dashboard-hero">
        <div className="portal-section-card__header">
          <div>
            <p className="portal-section-card__eyebrow">Resumo operacional</p>
            <h3>{club?.name || selectedClub.name}</h3>
          </div>
          <div className="portal-section-card__header-actions">
            <span className="portal-chip">Codigo {club?.code || selectedClub.code}</span>
            <span className="portal-chip">
              {lastSyncedAt ? `Ultimo sync ${formatDateTime(lastSyncedAt)}` : 'Sem sync ainda'}
            </span>
            <button
              type="button"
              className={`portal-secondary-button portal-secondary-button--compact portal-toggle-button ${
                liveRefreshEnabled ? 'portal-toggle-button--active' : ''
              }`}
              aria-pressed={liveRefreshEnabled}
              onClick={() => setLiveRefreshEnabled((current) => !current)}
            >
              {liveRefreshEnabled ? 'Tempo real: ligado' : 'Tempo real: desligado'}
            </button>
            <button
              type="button"
              className="portal-secondary-button portal-secondary-button--compact"
              onClick={() => void loadDashboardData()}
              disabled={loading || refreshing}
            >
              {loading || refreshing ? 'Atualizando...' : 'Atualizar agora'}
            </button>
          </div>
        </div>
        <p className="portal-muted">
          Visao inicial do club com dados reais do backend ja disponiveis para dono do club.
        </p>
        <div className="portal-dashboard-hero-stats">
          <article className="portal-dashboard-hero-stats__item">
            <span>Mesas ativas</span>
            <strong>{activeTables}</strong>
          </article>
          <article className="portal-dashboard-hero-stats__item">
            <span>Jogadores sentados</span>
            <strong>{activePlayers}</strong>
          </article>
          <article className="portal-dashboard-hero-stats__item">
            <span>Carteiras rastreadas</span>
            <strong>{counterSnapshot?.players.length || 0}</strong>
          </article>
          <article className="portal-dashboard-hero-stats__item">
            <span>Ultimo refresh</span>
            <strong>{loading ? 'Carregando' : 'Em dia'}</strong>
          </article>
        </div>
        {refreshing ? (
          <div className="portal-banner portal-banner--success">Dados sendo atualizados em tempo real.</div>
        ) : null}
        {error ? <div className="portal-banner portal-banner--error">{error}</div> : null}
      </section>

      <section className="portal-metrics-grid portal-dashboard-metrics-grid">
        <MetricCard
          label="Rake acumulado"
          value={formatCurrency(club?.total_rake_earned || 0)}
          tone="positive"
          helper="Valor consolidado do club"
          variant="dashboard"
          theme="rake"
          badge="Rake"
        />
        <MetricCard
          label="Mesas ativas"
          value={String(activeTables)}
          helper={`${tables.length} mesas em operacao ou historico recente`}
          variant="dashboard"
          theme="balance"
          badge="Mesas"
        />
        <MetricCard
          label="Membros"
          value={String(members.length)}
          helper="Membros retornados pelo backend"
          variant="dashboard"
          theme="deposit"
          badge="Equipe"
        />
        <MetricCard
          label="Saldos rastreados"
          value={formatCompactCurrency(totalWalletTracked)}
          tone="warning"
          helper="Carteiras visiveis no contador do club"
          variant="dashboard"
          theme="fee"
          badge="Financeiro"
        />
        <MetricCard
          label="Jogadores sentados"
          value={String(activePlayers)}
          helper="Soma de jogadores nas mesas listadas"
          variant="dashboard"
          theme="receivable"
          badge="Mesas"
        />
        <MetricCard
          label="Perfil de rake"
          value={club?.rake_profile_name || `${club?.rake_percentage || selectedClub.rake_percentage}%`}
          helper="Configuracao atual do club"
          variant="dashboard"
          theme="warning"
          badge="Perfil"
        />
      </section>

      <div className="portal-page-grid portal-page-grid--two-columns">
        <section className="portal-section-card portal-section-card--finance-highlight portal-section-card--finance-balance">
          <div className="portal-section-card__header">
            <div>
              <p className="portal-section-card__eyebrow">Mesas</p>
              <h3>Mesas mais recentes</h3>
            </div>
            <span className="portal-chip">{tables.length} mesa(s)</span>
          </div>
          <div className="portal-table-wrap">
            <table className="portal-table">
              <thead>
                <tr>
                  <th>Mesa</th>
                  <th>Jogo</th>
                  <th>Blinds</th>
                  <th>Status</th>
                  <th>Jogadores</th>
                  <th>Tempo de acao</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6}>Carregando mesas...</td>
                  </tr>
                ) : tables.length === 0 ? (
                  <tr>
                    <td colSpan={6}>Nenhuma mesa encontrada para este club.</td>
                  </tr>
                ) : (
                  tables.slice(0, 6).map((table) => (
                    <tr key={table.id}>
                      <td>
                        <div className="portal-table__cell-stack">
                          <strong>{table.name}</strong>
                          <span>{table.id}</span>
                        </div>
                      </td>
                      <td>{getGameTypeLabel(table.game_type)}</td>
                      <td>
                        {formatCurrency(table.smallBlind)} / {formatCurrency(table.bigBlind)}
                      </td>
                      <td>
                        <span className={`portal-status-pill ${getTableStatusTone(table.status)}`}>
                          {getTableStatusLabel(table.status)}
                        </span>
                      </td>
                      <td>
                        {table.currentPlayers}/{table.maxPlayers}
                      </td>
                      <td>{table.actionTimeLimitSeconds}s</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="portal-section-card portal-section-card--finance-highlight portal-section-card--finance-deposit">
          <div className="portal-section-card__header">
            <div>
              <p className="portal-section-card__eyebrow">Membros recentes</p>
              <h3>Operacao do club</h3>
            </div>
            <span className="portal-chip">{members.length} membro(s)</span>
          </div>
          <div className="portal-feed">
            {members.slice(0, 6).map((member, index) => (
              <article
                key={`${member.userId || 'member'}-${member.joinedAt || 'no-date'}-${member.role}-${index}`}
                className="portal-feed__item"
              >
                <div>
                  <strong>{member.userName}</strong>
                  <p>{getMemberRoleLabel(member.role)} | {member.status}</p>
                </div>
                <span>{formatDateTime(member.joinedAt)}</span>
              </article>
            ))}
            {members.length === 0 ? <p className="portal-muted">Nenhum membro retornado.</p> : null}
          </div>
        </section>
      </div>
    </div>
  );
};

export default DashboardPage;
